import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import TitleRow from '@/components/ui/TitleRow'
import { useSignalDashboard } from '@/features/signals/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import { loadSignalZone } from '@/screens/signals/SignalZoneScreen.loader'
import NepseSessionChart from '@/screens/signals/components/NepseSessionChart'
import SignalSummaryCard from '@/screens/signals/components/SignalSummaryCard'
import SignalsEmpty from '@/screens/signals/components/SignalsEmpty'
import SignalsLocked from '@/screens/signals/components/SignalsLocked'
import SignalsRouteError from '@/screens/signals/components/SignalsRouteError'
import {
  NepseSectionSkeleton,
  SummaryCardSkeleton,
} from '@/screens/signals/components/SignalZoneSkeletons'
import { signalsRailProps } from '@/screens/signals/components/signals-rail'
import { isSignalsLocked } from '@/screens/signals/lib/signals-ui'

const TITLE = 'Signal Zone'

/*
 * The loaded page, block for block: the counts panel, then the NEPSE section
 * (title, stat strip, chart), each cut from its loaded component's own boxes.
 */
function SignalZonePending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading Signal Zone…"
      header={<TitleRow title={TITLE} />}
      {...signalsRailProps(TITLE)}
    >
      <SummaryCardSkeleton />
      <NepseSectionSkeleton />
    </ScreenLayout>
  )
}

function SignalZoneError(props: ErrorComponentProps) {
  return (
    <SignalsRouteError
      {...props}
      header={<TitleRow title={TITLE} />}
      pageTitle={TITLE}
      title="Signal Zone did not load"
    />
  )
}

export const Route = createFileRoute('/_app/market/signals')({
  loader: loadSignalZone,
  pendingComponent: SignalZonePending,
  errorComponent: SignalZoneError,
  component: SignalZoneScreen,
})

/**
 * Signal Zone · Dashboard (f01) — the hub: the latest session's green and red
 * counts and its price-range counts in one panel, and NEPSE's section (the
 * session's market figures, then its candles), each a way into the page that
 * lists the names (Daily forecast, Breakout finder, the full chart).
 *
 * Always the latest session (owner ruling S1, 2026-10-29): no picker, so the
 * loader's read is the page's. `data: null` is a session with nothing
 * computed (a holiday), an answer and not an error; a 403 is the locked state
 * (D4); any other failure is region-local. The NEPSE section's bars are its
 * own read, so they never hold the page.
 */
function SignalZoneScreen() {
  const online = useOnline()
  const query = useSignalDashboard(null)
  const view = resolveViewState(query, { online })
  const dashboard = query.data

  return (
    <ScreenLayout header={<TitleRow title={TITLE} />} {...signalsRailProps(TITLE)}>
      {view === 'failed' || view === 'offline' ? (
        isSignalsLocked(query.error) ? (
          <SignalsLocked error={query.error} />
        ) : (
          <EmptyState
            kind={view}
            size="page"
            onRetry={() => void query.refetch()}
            retrying={query.isFetching}
            meta={view === 'failed' ? lastUpdatedMeta(query.dataUpdatedAt) : undefined}
            {...(view === 'failed' ? { title: 'Couldn’t load the signals' } : {})}
          />
        )
      ) : dashboard === undefined ? (
        <>
          <SummaryCardSkeleton />
          <NepseSectionSkeleton />
        </>
      ) : dashboard === null ? (
        <SignalsEmpty reason="session" date={null} />
      ) : (
        <>
          <SignalSummaryCard dashboard={dashboard} />
          <NepseSessionChart date={dashboard.date} isUpdating={false} market={dashboard.market} />
        </>
      )}
    </ScreenLayout>
  )
}
